/**
 * Opportunity Email Draft Tab Component
 * Auto-generates email drafts and allows iteration/editing
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, LoadingSpinner, HtmlRichTextEditor, useToastStore, usePermissions, canWriteOpportunities, useOptionalConfig } from '@tracekit/core';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';

async function downloadArtifactBlob(artifactId: string, filename: string): Promise<void> {
  const client = getAPIClient();
  const blob = await client.get<Blob>(`/artifacts/${artifactId}/download`, {
    responseType: 'blob' as any,
    timeout: 60000,
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || `artifact-${artifactId}`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
import {
  Mail,
  FileText,
  Send,
  Eye,
  X,
  Edit,
  RefreshCw,
  Save,
  AlertCircle,
  Download,
  HelpCircle,
  Code,
  Info,
  ExternalLink,
} from 'lucide-react';
import {
  formatAllowedDomainsHint,
  isAllowedRecipientDomain,
  normalizeAllowedEmailDomains,
  tabCompleteDomainSuffixes,
} from '../../utils/allowedEmailDomains';

interface OpportunityEmailDraftTabProps {
  opportunityId: string;
  projectId: string;
  opportunity: any; // Opportunity entity data
  /**
   * Official RFP posting URL — pass from OpportunityDetailModal (same computation as "View original source").
   * When set, takes precedence over resolving from `opportunity`.
   */
  sourcePostUrl?: string | null;
}

interface EmailDraft {
  subject: string;
  body: string;
  recipients: Array<{ name: string; email: string; id?: string; type?: string }>;
  attachments: Array<{ artifact_id: string; filename: string; name: string }>;
  summary: string;
  key_points: string[];
}

const RECIPIENT_HISTORY_KEY = 'email-draft-recipient-history';
const RECIPIENT_HISTORY_MAX = 20;

function getRecipientHistory(projectId: string): string[] {
  try {
    const raw = localStorage.getItem(`${RECIPIENT_HISTORY_KEY}-${projectId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed.filter((e) => typeof e === 'string' && e.includes('@')) : [];
  } catch {
    return [];
  }
}

function addToRecipientHistory(projectId: string, emails: string[]) {
  const valid = emails.filter((e) => e && e.trim() && e.includes('@')).map((e) => e.trim().toLowerCase());
  if (valid.length === 0) return;
  const current = getRecipientHistory(projectId);
  const combined = [...valid, ...current.filter((e) => !valid.includes(e))].slice(0, RECIPIENT_HISTORY_MAX);
  try {
    localStorage.setItem(`${RECIPIENT_HISTORY_KEY}-${projectId}`, JSON.stringify(combined));
  } catch {
    // ignore quota or other errors
  }
}

/**
 * Same resolution as OpportunityDetailModal "View original source" (official RFP posting URL).
 * Handles entity wrapper shape: `opportunity.data.url`, `opportunity.data.raw_data.detail_url`, etc.
 */
export function resolveSourcePostUrl(opportunity: any): string | null {
  if (!opportunity) return null;
  const data = opportunity.data ?? opportunity;
  const nestedRaw = (data && typeof data === 'object' ? (data as any).raw_data : null) ?? opportunity.raw_data;
  const raw =
    opportunity.url ??
    (data && typeof data === 'object' ? (data as any).url : undefined) ??
    (nestedRaw && typeof nestedRaw === 'object' ? (nestedRaw as any).detail_url : undefined) ??
    (typeof (opportunity as any).detail_url === 'string' ? (opportunity as any).detail_url : undefined);
  if (typeof raw === 'string' && (raw.startsWith('http://') || raw.startsWith('https://'))) {
    return raw.trim();
  }
  return null;
}

/**
 * Make the official posting URL clickable in HTML and append a callout if the URL is not present.
 * Used for preview and before send so the outreach email includes the source link.
 */
export function ensureSourceLinkInHtml(html: string, sourceUrl: string | null): string {
  if (!sourceUrl || !html) return html;
  const trimmed = sourceUrl.trim();
  if (!trimmed) return html;

  const esc = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`href=["']${esc}["']`, 'i').test(html)) return html;

  const hrefAttr = trimmed.replace(/"/g, '&quot;');
  const anchor = `<a href="${hrefAttr}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;font-weight:600;text-decoration:underline">${trimmed}</a>`;

  const idx = html.indexOf(trimmed);
  if (idx !== -1) {
    return html.slice(0, idx) + anchor + html.slice(idx + trimmed.length);
  }

  const linkBlock = `<p style="margin-top:16px;padding:12px;background:#eff6ff;border-radius:8px;border:1px solid #bfdbfe;font-size:14px;line-height:1.5">
    <strong>Official Opportunity Posting:</strong>
    <a href="${hrefAttr}" target="_blank" rel="noopener noreferrer" style="margin-left:8px;color:#1d4ed8;font-weight:600;text-decoration:underline">View original source</a>
  </p>`;
  if (/<\/body>/i.test(html)) {
    return html.replace(/<\/body>/i, `${linkBlock}</body>`);
  }
  return html + linkBlock;
}

/**
 * Append an internal deep-link so users can open the same opportunity modal.
 * The modal is opened by URL query param: `?opp_id=XXX`.
 */
export function ensureOpportunityModalLinkInHtml(
  html: string,
  opportunityModalHref: string | null,
  opportunityId: string
): string {
  if (!html) return html;
  if (!opportunityModalHref) return html;

  const hrefAttr = opportunityModalHref.replace(/"/g, '&quot;');
  // If block already exists (e.g. regenerated/edited draft), rewrite its href to current opportunity.
  if (html.includes('data-opportunity-modal-link="true"')) {
    const replacedDouble = html.replace(
      /(<p[^>]*data-opportunity-modal-link="true"[\s\S]*?<a[^>]*href=")([^"]*)(")/i,
      `$1${hrefAttr}$3`
    );
    if (replacedDouble !== html) return replacedDouble;

    const replacedSingle = html.replace(
      /(<p[^>]*data-opportunity-modal-link="true"[\s\S]*?<a[^>]*href=')([^']*)(')/i,
      `$1${opportunityModalHref}$3`
    );
    return replacedSingle;
  }

  const linkBlock = `<p data-opportunity-modal-link="true" style="margin-top:16px;padding:12px;background:#ecfeff;border-radius:8px;border:1px solid #a5f3fc;font-size:14px;line-height:1.5">
    <strong>View Opportunity:</strong>
    <a href="${hrefAttr}" target="_blank" rel="noopener noreferrer" style="margin-left:8px;color:#0e7490;font-weight:600;text-decoration:underline">Open opportunity</a>
  </p>`;

  if (/<\/body>/i.test(html)) {
    return html.replace(/<\/body>/i, `${linkBlock}</body>`);
  }
  return html + linkBlock;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(s: string): boolean {
  const t = (s || '').trim();
  return t.length > 0 && EMAIL_REGEX.test(t);
}
function parseEmailsFromInput(value: string): { valid: string[]; remainder: string } {
  const parts = value.split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
  const valid: string[] = [];
  let remainder = '';
  for (let i = 0; i < parts.length; i++) {
    if (isValidEmail(parts[i])) {
      valid.push(parts[i].trim().toLowerCase());
    } else {
      remainder = parts.slice(i).join(' ').trim();
      break;
    }
  }
  if (valid.length === parts.length) remainder = '';
  return { valid, remainder };
}

export function OpportunityEmailDraftTab({
  opportunityId,
  projectId,
  opportunity,
  sourcePostUrl: sourcePostUrlFromModal,
}: OpportunityEmailDraftTabProps) {
  const { addToast } = useToastStore();
  const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
  const canWriteOpp = !permissionsLoading && canWriteOpportunities(hasAnyPermission);
  const appConfig = useOptionalConfig();
  const allowedEmailDomains = React.useMemo(
    () => normalizeAllowedEmailDomains(appConfig?.customization?.allowedEmailDomains),
    [appConfig?.customization?.allowedEmailDomains]
  );
  const allowedDomainHint = React.useMemo(
    () => formatAllowedDomainsHint(allowedEmailDomains),
    [allowedEmailDomains]
  );
  const tabCompleteDomains = React.useMemo(
    () => tabCompleteDomainSuffixes(allowedEmailDomains),
    [allowedEmailDomains]
  );
  const emailIsAllowed = (value: string) => isAllowedRecipientDomain(value, allowedEmailDomains);
  const [emailChips, setEmailChips] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [recipientIds, setRecipientIds] = useState<string[]>([]); // Internal staff IDs (for future team picker)
  const [userRequestedDraft, setUserRequestedDraft] = useState(false); // KBF-025: only fetch when user clicks Generate
  const [regenerateVersion, setRegenerateVersion] = useState(0); // Increment on Regenerate so query key changes and backend gets force_regenerate: true
  const shouldAutoGenerateRef = useRef(false);
  const generateButtonRef = useRef<HTMLButtonElement>(null);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [showSourceView, setShowSourceView] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editSuggestions, setEditSuggestions] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  const [excludedAttachmentIds, setExcludedAttachmentIds] = useState<Set<string>>(new Set());
  const api = getAPIClient();

  useEffect(() => {
    if (!canWriteOpp) setIsEditing(false);
  }, [canWriteOpp]);

  // Fetch current user data to get full name for email signature
  const { data: currentUser } = useQuery<{
    id: string;
    email: string;
    full_name: string;
    username: string;
  }>({
    queryKey: ['current-user'],
    queryFn: async () => {
      const data = await api.get<{
        id: string;
        email: string;
        full_name: string;
        username: string;
      }>('/users/me');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const recipientHistory = React.useMemo(() => getRecipientHistory(projectId), [projectId]);
  /** Official RFP posting URL — prefer explicit prop from OpportunityDetailModal, then resolve from entity */
  const sourcePostUrl = React.useMemo(() => {
    if (typeof sourcePostUrlFromModal === 'string' && sourcePostUrlFromModal.trim()) {
      const t = sourcePostUrlFromModal.trim();
      if (t.startsWith('http://') || t.startsWith('https://')) return t;
    }
    return resolveSourcePostUrl(opportunity);
  }, [sourcePostUrlFromModal, opportunity]);

  // Canonical deep link for opening the same opportunity in External Data modal route.
  // Using `opp_id` keeps behavior consistent with dashboard deep-link handling.
  const opportunityModalHref = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      return new URL(`/rfp/external-data?opp_id=${encodeURIComponent(opportunityId)}`, window.location.origin).href;
    } catch {
      return null;
    }
  }, [opportunityId]);

  const effectiveAdditionalEmails = React.useMemo(
    () => emailChips.concat(isValidEmail(currentInput) ? [currentInput.trim().toLowerCase()] : []),
    [emailChips, currentInput]
  );
  
  const hasRecipients = recipientIds.length > 0 || effectiveAdditionalEmails.length > 0;

  // Current opportunity documents (Additional Documents section) - source of truth for attachments
  const { data: opportunityDocuments } = useQuery<{
    documents: Array<{ artifact_id: string; filename: string }>;
    total: number;
  }>({
    queryKey: ['opportunity-documents', opportunityId, projectId],
    queryFn: async () => {
      const data = await api.get<{
        documents: Array<{ artifact_id: string; filename: string }>;
        total: number;
      }>(`/api/opportunities/${opportunityId}/rfp-documents`, {
        params: { project_id: projectId },
      });
      if (!data?.documents) return { documents: [], total: 0 };
      return data;
    },
    enabled: !!opportunityId && !!projectId,
  });

  // KBF-025: Generate draft only when user has added recipients and clicks "Generate email draft" (no auto-fetch on mount)
  const { data: emailDraft, isLoading: generatingDraft, isError: draftError, error: draftErrorDetails, refetch: regenerateDraft } = useQuery<EmailDraft>({
    queryKey: ['email-draft', opportunityId, projectId, effectiveAdditionalEmails, recipientIds, regenerateVersion, currentUser?.full_name],
    queryFn: async ({ queryKey }) => {
      const [, oppId, projId, addEmails, recIds, regenVer, senderName] = queryKey as [string, string, string, string[], string[], number, string | undefined];
      const additionalRecipients = (addEmails || []).filter((e) => e && e.includes('@'));
      const ids = (recIds || []) as string[];
      const forceRegenerate = typeof regenVer === 'number' && regenVer > 0;
      const data = await api.post<EmailDraft>(
        `/api/opportunities/${oppId}/email-draft`,
        {
          recipient_ids: ids,
          additional_recipients: additionalRecipients,
          subject: undefined,
          include_documents: true,
          summary_length: 300,
          force_regenerate: forceRegenerate,
          sender_name: senderName || undefined,
        },
        { params: { project_id: projId } }
      );
      if (!data) throw new Error('Empty response from email draft API');
      if (!data.subject || !data.body) throw new Error('Invalid response from email draft API - missing subject or body');
      const PROMPT_LEAK_PREFIXES = ['you are', 'generate a', 'generate an', 'write a', 'write an', 'create a', 'create an'];
      const bodyLower = data.body.trim().toLowerCase();
      if (data.body.length < 50 || PROMPT_LEAK_PREFIXES.some(p => bodyLower.startsWith(p))) {
        throw new Error('Email draft body appears to contain prompt text rather than email content — please try regenerating.');
      }
      return data;
    },
    enabled: !!opportunityId && !!projectId && userRequestedDraft && hasRecipients,
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 10 * 60 * 1000,
  });

  /** Preview HTML with clickable source link (does not mutate stored draft text) */
  const emailPreviewHtml = React.useMemo(
    () => {
      const baseHtml = ensureSourceLinkInHtml(editedBody || emailDraft?.body || '', sourcePostUrl);
      return ensureOpportunityModalLinkInHtml(baseHtml, opportunityModalHref, opportunityId);
    },
    [editedBody, emailDraft?.body, sourcePostUrl, opportunityModalHref, opportunityId]
  );

  // Attachments = current opportunity documents only (so newly added docs appear without regenerating draft)
  const effectiveAttachments = React.useMemo(
    () =>
      (opportunityDocuments?.documents ?? []).map((d) => ({
        artifact_id: d.artifact_id,
        filename: d.filename,
        name: d.filename,
      })),
    [opportunityDocuments?.documents]
  );

  // Sync edited values from server draft only when draft changes (e.g. Generate/Regenerate).
  // Do not depend on isEditing: when user clicks Save we must not overwrite their edits.
  useEffect(() => {
    if (emailDraft) {
      setEditedSubject(emailDraft.subject);
      setEditedBody(emailDraft.body);
    }
  }, [emailDraft]);

  const addChipFromInput = (triggerGenerate: boolean = false) => {
    const t = currentInput.trim();
    if (!t) {
      setInputError(null);
      if (triggerGenerate && hasRecipients) shouldAutoGenerateRef.current = true;
      return;
    }
    if (!isValidEmail(t)) {
      setInputError('Please enter a valid email address (e.g. name@company.com)');
      return;
    }
    if (!emailIsAllowed(t)) {
      setInputError(`Only ${allowedDomainHint} email addresses are allowed.`);
      addToast({
        type: 'error',
        title: 'Invalid recipient domain',
        message: `Only ${allowedDomainHint} recipients are allowed.`,
        duration: 5000,
      });
      return;
    }
    const normalized = t.toLowerCase();
    if (emailChips.includes(normalized)) {
      setCurrentInput('');
      setInputError(null);
      if (triggerGenerate) shouldAutoGenerateRef.current = true;
      return;
    }
    setEmailChips((prev) => [...prev, normalized]);
    setCurrentInput('');
    setInputError(null);
    if (triggerGenerate) shouldAutoGenerateRef.current = true;
  };

  const handleRecipientInputChange = (value: string) => {
    setCurrentInput(value);
    setInputError(null);
    if (value.includes(',')) {
      const { valid, remainder } = parseEmailsFromInput(value);
      if (valid.length > 0) {
        const invalidDomain = valid.filter((e) => !emailIsAllowed(e));
        if (invalidDomain.length > 0) {
          setInputError(`Only ${allowedDomainHint} email addresses are allowed.`);
          addToast({
            type: 'error',
            title: 'Invalid recipient domain',
            message: `Remove external emails: ${invalidDomain.join(', ')}`,
            duration: 7000,
          });
          setCurrentInput(remainder);
          return;
        }
        setEmailChips((prev) => {
          const next = [...prev];
          valid.forEach((e) => {
            if (!next.includes(e)) next.push(e);
          });
          return next;
        });
        setCurrentInput(remainder);
      }
    }
  };

  const handleRecipientKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addChipFromInput(true);
      return;
    }
    if (e.key === 'Tab') {
      const t = currentInput.trim();
      const cycleForward = !e.shiftKey;
      if (t && !t.includes('@')) {
        e.preventDefault();
        const localPart = t;
        const currentDomain = tabCompleteDomains.find((d) => localPart.endsWith(d));
        const currentIndex = currentDomain ? tabCompleteDomains.indexOf(currentDomain) : -1;
        const basePart = currentIndex >= 0 ? localPart.slice(0, -tabCompleteDomains[currentIndex].length) : localPart;
        const nextIndex =
          currentIndex < 0 ? (cycleForward ? 0 : tabCompleteDomains.length - 1) : (currentIndex + (cycleForward ? 1 : -1) + tabCompleteDomains.length) % tabCompleteDomains.length;
        setCurrentInput(basePart + tabCompleteDomains[nextIndex]);
        setInputError(null);
        return;
      }
      if (t.includes('@')) {
        const atIdx = t.indexOf('@');
        const localPart = t.slice(0, atIdx).trim();
        const afterAt = t.slice(atIdx);
        const currentDomain = tabCompleteDomains.find((d) => afterAt === d || afterAt.startsWith(d));
        const currentIndex = currentDomain ? tabCompleteDomains.indexOf(currentDomain) : -1;
        const nextIndex =
          currentIndex < 0 ? (cycleForward ? 0 : tabCompleteDomains.length - 1) : (currentIndex + (cycleForward ? 1 : -1) + tabCompleteDomains.length) % tabCompleteDomains.length;
        e.preventDefault();
        setCurrentInput(localPart + tabCompleteDomains[nextIndex]);
        setInputError(null);
        return;
      }
    }
  };

  const removeChip = (index: number) => {
    setEmailChips((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!shouldAutoGenerateRef.current) return;
    shouldAutoGenerateRef.current = false;
    if (recipientIds.length > 0 || emailChips.length > 0 || isValidEmail(currentInput)) {
      setUserRequestedDraft(true);
    }
  }, [emailChips, currentInput, recipientIds.length]);

  const handleGenerateDraft = () => {
    if (!hasRecipients) return;
    const invalidRecipients = effectiveAdditionalEmails.filter((e) => !emailIsAllowed(e));
    if (invalidRecipients.length > 0) {
      setInputError(`Only ${allowedDomainHint} email addresses are allowed.`);
      addToast({
        type: 'error',
        title: 'Cannot generate draft',
        message: `External recipients are not allowed: ${invalidRecipients.join(', ')}`,
        duration: 7000,
      });
      return;
    }
    setUserRequestedDraft(true);
    // Query runs automatically when enabled becomes true (userRequestedDraft && hasRecipients)
  };

  const handleRegenerateDraft = () => {
    setExcludedAttachmentIds(new Set());
    setIsEditing(false);
    setEditSuggestions('');
    setRegenerateVersion((v) => v + 1);
  };

  const handleApplySuggestions = async () => {
    if (!editSuggestions.trim() || !emailDraft) return;
    setApplyingSuggestions(true);
    try {
      const response = await api.post(
        `/api/opportunities/${opportunityId}/email-draft/regenerate`,
        {
          base_draft: emailDraft,
          suggestions: editSuggestions,
          edited_subject: editedSubject !== emailDraft.subject ? editedSubject : undefined,
          edited_body: editedBody !== emailDraft.body ? editedBody : undefined,
        },
        { params: { project_id: projectId } }
      );
      // API client may return full axios response or unwrapped data
      const data = response?.data != null ? response.data : response;
      if (data?.subject != null && data?.body != null) {
        setEditedSubject(data.subject);
        setEditedBody(data.body);
        setEditSuggestions('');
        setIsEditing(false);
      }
    } catch (error: any) {
      console.error('[EmailDraft] Apply suggestions failed:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message ?? error.message;
      addToast({
        type: 'error',
        title: 'Could not apply suggestions',
        message,
        duration: 6000,
      });
    } finally {
      setApplyingSuggestions(false);
    }
  };

  // Convert markdown to HTML for email body
  const convertMarkdownToHTML = (text: string): string => {
    if (!text) return '';
    
    let html = text;
    
    // Escape existing HTML first (but preserve already-formatted HTML)
    // Only escape if it doesn't look like HTML already
    if (!html.includes('<') || html.match(/<[^>]+>/g)?.length === 0) {
      html = html
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
    
    // Headers (must be on new lines)
    html = html.replace(/^### (.+)$/gm, '<h3 style="font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; color: #111827;">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 style="font-size: 20px; font-weight: 700; margin-top: 20px; margin-bottom: 12px; color: #111827;">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 style="font-size: 24px; font-weight: 700; margin-top: 24px; margin-bottom: 16px; color: #111827;">$1</h1>');
    
    // Bold (before italic to handle **)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong style="font-weight: 600;">$1</strong>');
    
    // Italic
    html = html.replace(/\*([^*]+)\*/g, '<em style="font-style: italic;">$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em style="font-style: italic;">$1</em>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: underline;">$1</a>');
    
    // Code blocks
    html = html.replace(/```([^`]+)```/g, '<pre style="background-color: #f3f4f6; padding: 12px; border-radius: 4px; overflow-x: auto; margin: 12px 0;"><code style="font-family: monospace; font-size: 14px;">$1</code></pre>');
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code style="background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 14px;">$1</code>');
    
    // Process lists and paragraphs line by line
    const lines = html.split('\n');
    const processedLines: string[] = [];
    let inUl = false;
    let inOl = false;
    let currentParagraph: string[] = [];
    
    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        processedLines.push(`<p style="margin: 12px 0; line-height: 1.6;">${currentParagraph.join(' ')}</p>`);
        currentParagraph = [];
      }
    };
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      if (!trimmed) {
        flushParagraph();
        if (inUl) {
          processedLines.push('</ul>');
          inUl = false;
        }
        if (inOl) {
          processedLines.push('</ol>');
          inOl = false;
        }
        continue;
      }
      
      // Check if it's already a tag (header, code block, etc.)
      if (trimmed.startsWith('<h') || trimmed.startsWith('<pre') || trimmed.startsWith('<code')) {
        flushParagraph();
        if (inUl) {
          processedLines.push('</ul>');
          inUl = false;
        }
        if (inOl) {
          processedLines.push('</ol>');
          inOl = false;
        }
        processedLines.push(trimmed);
        continue;
      }
      
      // Check for bullet list
      const bulletMatch = trimmed.match(/^[-*•]\s+(.+)$/);
      if (bulletMatch) {
        flushParagraph();
        if (inOl) {
          processedLines.push('</ol>');
          inOl = false;
        }
        if (!inUl) {
          processedLines.push('<ul style="margin: 12px 0; padding-left: 24px; list-style-type: disc;">');
          inUl = true;
        }
        processedLines.push(`<li style="margin-bottom: 4px;">${bulletMatch[1]}</li>`);
        continue;
      }
      
      // Check for numbered list
      const numberedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
      if (numberedMatch) {
        flushParagraph();
        if (inUl) {
          processedLines.push('</ul>');
          inUl = false;
        }
        if (!inOl) {
          processedLines.push('<ol style="margin: 12px 0; padding-left: 24px; list-style-type: decimal;">');
          inOl = true;
        }
        processedLines.push(`<li style="margin-bottom: 4px;">${numberedMatch[1]}</li>`);
        continue;
      }
      
      // Regular text - add to paragraph
      flushParagraph();
      if (inUl) {
        processedLines.push('</ul>');
        inUl = false;
      }
      if (inOl) {
        processedLines.push('</ol>');
        inOl = false;
      }
      currentParagraph.push(trimmed);
    }
    
    flushParagraph();
    if (inUl) {
      processedLines.push('</ul>');
    }
    if (inOl) {
      processedLines.push('</ol>');
    }
    
    html = processedLines.join('\n');
    
    return html;
  };

  const removeAttachmentFromDraft = (artifactId: string) => {
    setExcludedAttachmentIds((prev) => new Set(prev).add(artifactId));
  };

  const sendEmailMutation = useMutation({
    mutationFn: async (): Promise<{ response: any; sentWithoutAttachments?: boolean }> => {
      if (!emailDraft) {
        throw new Error('No email draft available');
      }

      const toRecipients = effectiveAdditionalEmails.filter((e) => e && e.trim() && e.includes('@'));
      if (toRecipients.length === 0) {
        throw new Error('Please add at least one recipient email address');
      }
      const invalidRecipients = toRecipients.filter((e) => !emailIsAllowed(e));
      if (invalidRecipients.length > 0) {
        throw new Error(
          `Only ${allowedDomainHint} recipients are allowed. Invalid: ${invalidRecipients.join(', ')}`
        );
      }

      const attachmentIds = effectiveAttachments
        .filter((att) => !excludedAttachmentIds.has(att.artifact_id))
        .map((att) => att.artifact_id);

      let emailBody = editedBody || emailDraft.body;
      if (emailBody && (emailBody.includes('**') || emailBody.includes('__') || emailBody.includes('`') || emailBody.match(/^#+\s/m))) {
        if (!emailBody.includes('<') || emailBody.match(/<[^>]+>/g)?.length === 0) {
          emailBody = convertMarkdownToHTML(emailBody);
        } else {
          emailBody = emailBody
            .replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
            .replace(/__([^_]+)__/g, '<strong style="font-weight: 600;">$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em style="font-style: italic;">$1</em>')
            .replace(/_([^_]+)_/g, '<em style="font-style: italic;">$1</em>');
        }
      }

      emailBody = ensureSourceLinkInHtml(emailBody, sourcePostUrl);
      emailBody = ensureOpportunityModalLinkInHtml(emailBody, opportunityModalHref, opportunityId);

      const payload = {
        to_recipients: toRecipients,
        cc_recipients: [] as string[],
        subject: editedSubject || emailDraft.subject,
        body: emailBody,
        attachment_artifact_ids: attachmentIds,
      };

      // Try with attachments first (normal case)
      try {
        const response = await api.post(
          `/api/opportunities/${opportunityId}/email-draft/send`,
          payload,
          { params: { project_id: projectId } }
        );
        return { response, sentWithoutAttachments: false };
      } catch (firstError: any) {
        // Only retry without attachments when the failure is likely attachment-related (not timeouts, 5xx, etc.)
        const status = firstError?.response?.status as number | undefined;
        const detail = String(firstError?.response?.data?.detail ?? firstError?.message ?? '');
        const likelyAttachmentIssue =
          status === 400 ||
          status === 404 ||
          status === 413 ||
          status === 422 ||
          /artifact|attachment|not found|too large|invalid.*id|download/i.test(detail);
        if (attachmentIds.length > 0 && likelyAttachmentIssue) {
          try {
            const fallbackPayload = { ...payload, attachment_artifact_ids: [] };
            const response = await api.post(
              `/api/opportunities/${opportunityId}/email-draft/send`,
              fallbackPayload,
              { params: { project_id: projectId } }
            );
            return { response, sentWithoutAttachments: true };
          } catch (retryError: any) {
            throw retryError;
          }
        }
        throw firstError;
      }
    },
    onSuccess: (result, variables, context) => {
      const { response, sentWithoutAttachments } = result;
      console.log('[EmailDraft] Email sent successfully:', response);
      const toRecipients = effectiveAdditionalEmails.filter((e) => e && e.trim() && e.includes('@'));
      if (toRecipients.length > 0) addToRecipientHistory(projectId, toRecipients);
      if (sentWithoutAttachments) {
        addToast({
          type: 'warning',
          title: 'Email sent with warnings',
          message:
            (response?.message || 'Email sent successfully.') +
            ' Attachments could not be included and the email was sent without them.',
          duration: 7000,
        });
      } else {
        addToast({
          type: 'success',
          title: 'Email sent',
          message: response?.message || 'Email sent successfully!',
          duration: 5000,
        });
      }
    },
    onError: (error: any) => {
      console.error('[EmailDraft] Failed to send email:', error);
      addToast({
        type: 'error',
        title: 'Email send failed',
        message: error.response?.data?.detail || error.message || 'Unknown error',
        duration: 7000,
      });
    },
  });

  const handleSendEmail = () => {
    if (!emailDraft) {
      addToast({
        type: 'error',
        title: 'Draft not ready',
        message: 'No email draft available. Please wait for the draft to generate.',
        duration: 5000,
      });
      return;
    }
    
    const toRecipients = effectiveAdditionalEmails.filter((e) => e && e.includes('@'));
    if (toRecipients.length === 0) {
      addToast({
        type: 'warning',
        title: 'No recipients',
        message: 'Please add at least one recipient email address before sending.',
        duration: 5000,
      });
      return;
    }
    const invalidRecipients = toRecipients.filter((e) => !emailIsAllowed(e));
    if (invalidRecipients.length > 0) {
      setInputError(`Only ${allowedDomainHint} email addresses are allowed.`);
      addToast({
        type: 'error',
        title: 'Cannot send email to outsiders',
        message: `Remove external recipients: ${invalidRecipients.join(', ')}`,
        duration: 7000,
      });
      return;
    }

    addToast({
      type: 'info',
      title: 'Sending email',
      message: `Sending to ${toRecipients.length} recipient(s)...`,
      duration: 2500,
    });
    sendEmailMutation.mutate();
  };

  // Internal team members (primary TO recipients) - those with an 'id' field (staff entity IDs)
  const internalRecipients = emailDraft?.recipients?.filter(r => r.id && r.type !== 'client_contact' && r.type !== 'client_contact_placeholder') || [];

  // User-friendly error message; 401 = auth issue (session/API key)
  const statusCode = (draftErrorDetails as any)?.response?.status;
  const rawMessage = draftErrorDetails?.message || (draftErrorDetails as any)?.response?.data?.detail || '';
  const draftErrorMessage =
    statusCode === 401
      ? "Your session may have expired or you're not authorized. Please sign in again or refresh the page and ensure your API key is set, then retry."
      : typeof rawMessage === 'string'
        ? rawMessage
        : String(rawMessage || 'An error occurred.');
  const isNoValidRecipients = typeof draftErrorMessage === 'string' && draftErrorMessage.toLowerCase().includes('no valid recipients');
  const is401 = statusCode === 401;

  return (
    <div className="space-y-6">
      {/* Flow hint: best results after uploading documents */}
      <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700">
        <p className="text-sm text-primary-800 dark:text-primary-200 flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <span>Draft an email to your team using this opportunity. For best results, upload RFP documents in the <strong>Documents</strong> tab first.</span>
        </p>
      </div>
      {/* KBF-025: Recipients section always visible first - add team members, then click Generate */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          Email Recipients
        </h3>
        <p className="text-sm text-gray-600 dark:text-slate-200 mb-4">
          Add at least one recipient (email or internal team), then click &quot;Generate email draft&quot; once to create the draft.
          After it&apos;s generated, use <strong>Regenerate</strong> in the draft card below to refresh — the same button is disabled so you don&apos;t duplicate work.
        </p>

        {/* Primary Recipients (Internal Team - TO) - shown after draft if API returned any */}
        {emailDraft && internalRecipients.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
              Primary Recipients (TO) - Internal Team
            </label>
            <div className="space-y-2">
              {internalRecipients.map((recipient, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-slate-100">{recipient.name || recipient.email}</p>
                    <p className="text-sm text-gray-600 dark:text-slate-200">{recipient.email}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional recipients (emails) - chips + single input, comma/Enter to add, suggested emails from history */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
            Recipients (email addresses)
          </label>
          <p className="text-xs text-gray-500 dark:text-slate-300 mb-2">
            Type an email and press Enter to add as a chip (then Generate runs). Use a comma to add multiple. Only <strong>{allowedDomainHint}</strong> recipients are allowed.
          </p>
          <div className="flex flex-wrap gap-2 items-center p-2 min-h-[42px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
            {emailChips.map((addr, index) => (
              <span
                key={`${addr}-${index}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm"
              >
                {addr}
                {canWriteOpp && (
                <button
                  type="button"
                  onClick={() => removeChip(index)}
                  className="p-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800/50"
                  aria-label={`Remove ${addr}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                )}
              </span>
            ))}
            <div className="relative flex-1 min-w-[180px] inline-flex">
              <input
                type="text"
                inputMode="email"
                value={currentInput}
                onChange={(e) => handleRecipientInputChange(e.target.value)}
                onKeyDown={handleRecipientKeyDown}
                disabled={!canWriteOpp}
                onFocus={() => setSuggestionsOpen(true)}
                onBlur={() => {
                  const t = currentInput.trim();
                  if (t && !isValidEmail(t)) setInputError('Please enter a valid email address (e.g. name@company.com)');
                  else if (t && !emailIsAllowed(t)) setInputError(`Only ${allowedDomainHint} email addresses are allowed.`);
                  setTimeout(() => setSuggestionsOpen(false), 180);
                }}
                placeholder={emailChips.length === 0 ? 'name@company.com or paste multiple (comma-separated)' : 'Add another...'}
                className={`flex-1 min-w-0 px-2 py-1 bg-transparent border-0 text-sm focus:outline-none focus:ring-0 ${
                  inputError ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-slate-100'
                }`}
                autoComplete="off"
              />
              {suggestionsOpen && (recipientHistory.length > 0 || (currentInput.trim() && !currentInput.includes('@'))) && (
                <ul
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-md border border-theme-border bg-white dark:bg-gray-800 shadow-lg py-1 text-sm"
                  role="listbox"
                >
                  {currentInput.trim() && !currentInput.includes('@') &&
                    tabCompleteDomains.map((domain) => {
                      const addr = (currentInput.trim() + domain).toLowerCase();
                      if (emailChips.includes(addr)) return null;
                      return (
                        <li key={addr}>
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-left text-gray-900 dark:text-slate-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setEmailChips((prev) => (prev.includes(addr) ? prev : [...prev, addr]));
                              setCurrentInput('');
                              setInputError(null);
                              setSuggestionsOpen(false);
                              shouldAutoGenerateRef.current = true;
                            }}
                          >
                            {addr}
                          </button>
                        </li>
                      );
                    })}
                  {recipientHistory
                    .filter(
                      (h) =>
                        h &&
                        (currentInput.trim() === '' || h.toLowerCase().includes(currentInput.trim().toLowerCase())) &&
                        h.toLowerCase() !== currentInput.trim().toLowerCase() &&
                        !emailChips.includes(h)
                    )
                    .slice(0, 8)
                    .map((addr) => (
                      <li key={addr}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-gray-900 dark:text-slate-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEmailChips((prev) => (prev.includes(addr) ? prev : [...prev, addr]));
                            setCurrentInput('');
                            setInputError(null);
                            setSuggestionsOpen(false);
                            shouldAutoGenerateRef.current = true;
                          }}
                        >
                          {addr}
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
          {inputError && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
              {inputError}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {canWriteOpp ? (
          <Button
            variant={emailDraft ? 'outline' : 'primary'}
            onClick={handleGenerateDraft}
            disabled={!hasRecipients || generatingDraft || !!emailDraft}
            title={
              !hasRecipients
                ? 'Add at least one recipient email above'
                : emailDraft
                  ? 'Draft already generated — use Regenerate in the Email Draft section below'
                  : 'Generate email draft'
            }
          >
            {generatingDraft ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Generating...
              </>
            ) : emailDraft ? (
              <>
                <Mail className="w-4 h-4 mr-2 opacity-70" />
                Draft generated
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Generate email draft
              </>
            )}
          </Button>
          ) : (
            <p className="text-sm text-gray-600 dark:text-slate-400">You have read-only access. Generating or changing email drafts requires opportunity write permission.</p>
          )}
          {canWriteOpp && !hasRecipients && (
            <span className="text-sm text-amber-700 dark:text-amber-300">
              Add at least one recipient email above to enable Generate.
            </span>
          )}
          {canWriteOpp && emailDraft && !generatingDraft && (
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Use <strong>Regenerate</strong> under Email Draft to create a new version.
            </span>
          )}
        </div>
      </Card>

      {/* Loading state */}
      {generatingDraft && (
        <Card className="p-6">
          <div className="flex items-center justify-center py-6">
            <LoadingSpinner />
            <span className="ml-3 text-gray-600 dark:text-slate-200">Generating email draft...</span>
          </div>
        </Card>
      )}

      {/* Error state - with KBF-025 inline message for "No valid recipients" */}
      {draftError && !generatingDraft && (
        <Card className="p-6">
          <div className="text-center py-6">
            <AlertCircle className="w-12 h-12 text-red-400 dark:text-red-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-slate-200 mb-2">Failed to generate email draft.</p>
            {isNoValidRecipients ? (
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                Please add at least one recipient (enter email above) and click Generate again.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{draftErrorMessage}</p>
                {is401 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    If using an API key, set it in Settings or via the environment (e.g. VITE_MASTER_API_KEY) and refresh.
                  </p>
                )}
              </>
            )}
            {canWriteOpp && (
            <Button variant="primary" onClick={() => handleGenerateDraft()} disabled={!hasRecipients}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            )}
          </div>
        </Card>
      )}

      {/* Draft content - shown after successful generation */}
      {emailDraft && emailDraft.subject && emailDraft.body && !generatingDraft ? (
        <>

          {/* Email Draft - Auto-generated with Edit Capability */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Email Draft
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
                {canWriteOpp && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerateDraft}
                  disabled={generatingDraft}
                  className="text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                )}
                <div className="relative flex items-center gap-2">
                  {canWriteOpp && (
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={handleSendEmail}
                    disabled={sendEmailMutation.isPending || !emailDraft || effectiveAdditionalEmails.length === 0}
                    title={effectiveAdditionalEmails.length === 0 ? 'Please add at least one recipient email address' : 'Send email'}
                  >
                    {sendEmailMutation.isPending ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                  )}
                  {effectiveAdditionalEmails.length === 0 && emailDraft && (
                    <div className="group relative">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                      <div className="absolute bottom-full right-0 transform -translate-x-[80%] mb-2 w-64 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        Add at least one recipient email above to enable sending
                        <div className="absolute top-full right-0 transform -translate-x-[80%] -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Subject - Editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
                  Subject
                </label>
                {canWriteOpp && isEditing ? (
                  <input
                    type="text"
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  />
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                    <p className="text-gray-900 dark:text-slate-100 font-medium">
                      {editedSubject || emailDraft.subject}
                    </p>
                    {canWriteOpp && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Email Body - WYSIWYG rich text by default, optional View source (KBF-117) */}
              <div>
                {sourcePostUrl && (
                  <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50/80 dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-900 dark:text-blue-100">
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>
                      Preview adds a clickable <strong>official posting</strong> link (same URL as &quot;View original source&quot; on the Overview tab). It is included when you send.
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">
                    Email Body
                  </label>
                  {canWriteOpp && isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSourceView(!showSourceView)}
                      title={showSourceView ? 'Hide source (edit as formatted text)' : 'View source (edit raw HTML)'}
                    >
                      <Code className="w-4 h-4 mr-1" />
                      {showSourceView ? 'Hide source' : 'View source'}
                    </Button>
                  )}
                </div>
                {canWriteOpp && isEditing ? (
                  showSourceView ? (
                    <textarea
                      value={editedBody || emailDraft.body}
                      onChange={(e) => setEditedBody(e.target.value)}
                      rows={14}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 font-mono text-xs text-gray-800 dark:text-slate-100"
                      placeholder="Raw HTML"
                      spellCheck={false}
                    />
                  ) : (
                    <HtmlRichTextEditor
                      value={editedBody || emailDraft.body || ''}
                      onChange={setEditedBody}
                      placeholder="Email content..."
                      minHeight="280px"
                      compact
                    />
                  )
                ) : showPreview ? (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800 min-h-[120px]">
                    <div
                      className="prose prose-slate max-w-none text-sm dark:prose-invert [&_a]:text-blue-600 [&_a]:font-semibold [&_a]:underline"
                      dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                    <p className="text-sm text-gray-600 dark:text-slate-200">
                      Preview hidden. Click &quot;Show Preview&quot; or &quot;Edit&quot; to view
                    </p>
                    {canWriteOpp && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Edit mode: Save (keep edits) and Cancel (discard) - always visible when editing */}
              {canWriteOpp && isEditing && (
                <div className="flex flex-wrap items-center gap-2 pt-2 pb-2 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setShowSourceView(false);
                    }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditedSubject(emailDraft.subject);
                      setEditedBody(emailDraft.body);
                      setIsEditing(false);
                      setShowSourceView(false);
                      setEditSuggestions('');
                    }}
                  >
                    Cancel
                  </Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Save keeps your edits; Cancel reverts to the last draft.
                  </span>
                </div>
              )}

              {/* Suggestions for Improvement (optional) - when in edit mode */}
              {isEditing && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
                    Suggestions for Improvement (optional)
                  </label>
                  <textarea
                    value={editSuggestions}
                    onChange={(e) => setEditSuggestions(e.target.value)}
                    placeholder="E.g., 'Make the tone more formal', 'Add more details about the deadline', 'Emphasize our past experience'"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  />
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleApplySuggestions}
                      disabled={!editSuggestions.trim() || applyingSuggestions}
                    >
                      {applyingSuggestions ? (
                        <LoadingSpinner size="sm" className="mr-2" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Apply Suggestions
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditSuggestions('')}
                    >
                      Clear suggestions
                    </Button>
                  </div>
                </div>
              )}

              {/* Attachments - only documents in Additional Documents section; remove/cancel like recipient chips */}
              {effectiveAttachments.length > 0 && (() => {
                const visibleAttachments = effectiveAttachments.filter(
                  (att) => !excludedAttachmentIds.has(att.artifact_id)
                );
                if (visibleAttachments.length === 0) return null;
                return (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
                      Attachments ({visibleAttachments.length})
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {visibleAttachments.map((att, idx) => (
                        <div
                          key={att.artifact_id ?? idx}
                          className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                          <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                          <span className="text-sm text-gray-900 dark:text-slate-100 truncate flex-1" title={att.name || att.filename}>
                            {att.name || att.filename}
                          </span>
                          {att.artifact_id && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  downloadArtifactBlob(att.artifact_id, att.name || att.filename || `attachment-${att.artifact_id}`);
                                }}
                                className="flex-shrink-0"
                                aria-label="Download"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAttachmentFromDraft(att.artifact_id)}
                                className="flex-shrink-0 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 p-0.5"
                                aria-label="Remove attachment"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}
